import { AxiosError } from 'axios';
import { IRootStore } from 'RootStore';
import { types, flow, Instance, IStateTreeNode, getRoot } from 'mobx-state-tree';
import { filterPOSTData, getQueryString } from 'utils/prepareData';
import axios from 'utils/axios.config';
import { withAlertStore } from 'components/Core/CustomAlert/store';
import { IBrandBasicForm } from 'components/Forms/BrandBasicForm/types';
import { IBrandsCataloguesFiltersFormFields } from 'components/Forms/BrandsCataloguesFiltersForm/types';
import { ErrorData, StatusTypeReq, /* IQueryWithSearch ,*/ IResponse } from 'types/common';
import {
  IBrandBasicData,
  IBrandListData,
  IBrandsCatalogue,
  IBrandsCatalogueWithPages,
  IParsersBrandsAutocompleteQuery,
  IQueryBrandData,
} from './types';
const { model, frozen, maybeNull, boolean } = types;

export const BrandsStore = model('BrandsStore', {
  brands: maybeNull(frozen<IBrandListData[]>()),
  brandsQuery: frozen<IQueryBrandData>(),
  isBrand: boolean,
  brandBasicData: maybeNull(frozen<IBrandBasicData>()),
  brandsCatalogues: maybeNull(frozen<IBrandsCatalogue[]>()),
  brandCataloguesFilters: frozen<IBrandsCataloguesFiltersFormFields>(),
  brandsCatalogue: maybeNull(frozen<IBrandsCatalogueWithPages>()),
  elasticCountsMap: frozen<Record<string, number>>(),
})
  .extend(withAlertStore)
  .views((self) => ({
    get filteredBrandCatalogues() {
      if (!self.brandsCatalogues) return [];
      const { search, status, dublicatedStatus } = self.brandCataloguesFilters;
      return self.brandsCatalogues.reduce((acc, item) => {
        let filteredItem: IBrandsCatalogue | null = item;
        if (status) {
          filteredItem =
            (status.id === 'Active') === item.isActive || (status.id === 'Inactive') === !item.isActive ? item : null;
        }
        if (dublicatedStatus && filteredItem) {
          filteredItem =
            (dublicatedStatus.id === 'dublicated') === item.isDuplicated ||
            (dublicatedStatus.id === 'notDublicated') === !item.isDuplicated
              ? item
              : null;
        }
        if (search && filteredItem) {
          filteredItem = item.name.includes(search) ? item : null;
        }
        if (filteredItem) {
          acc.push(filteredItem);
        }
        return acc;
      }, [] as IBrandsCatalogue[]);
    },
    getCataloguePage(pageNumber: number) {
      return self.brandsCatalogue?.pages[pageNumber];
    },
  }))
  .actions((self) => ({
    setBrands(data: IBrandListData[]) {
      const countsMap = self.elasticCountsMap;
      if (countsMap && Object.keys(countsMap).length > 0) {
        self.brands = data.map((brand) => ({
          ...brand,
          numberOfProductsInElastic: countsMap[brand.id] ?? brand.numberOfProductsInElastic ?? 0,
        }));
      } else {
        self.brands = data;
      }
    },
    setElasticCountsMap(data: Record<string, number>) {
      self.elasticCountsMap = data;
    },
    addBrands(data: IBrandListData[]) {
      self.brands = [...(self.brands || []), ...data];
    },
    updateBrandsQuery(query: Partial<IQueryBrandData>) {
      self.brandsQuery = { ...self.brandsQuery, ...query };
    },
    setBasicBrandData(data: IBrandBasicData) {
      self.brandBasicData = data;
      self.isBrand = true;
      return data;
    },
    setBrandsCatalogues(data: IBrandsCatalogue[]) {
      self.brandsCatalogues = data;
      // self.brandsCatalogues = data.map(
      //   (item, index) => ({ ...item, isDuplicated: index % 2 ? true : false }) // Example of setting isDuplicated, adjust as needed
      // );
    },
    updateBrandCataloguesFilters(data: IBrandsCataloguesFiltersFormFields) {
      self.brandCataloguesFilters = { ...self.brandCataloguesFilters, ...data };
    },
    setBrandsCatalogue(data: IBrandsCatalogueWithPages) {
      self.brandsCatalogue = data;
    },
    clearData() {
      self.brandBasicData = null;
      self.isBrand = false;
    },
    errorHandling({ response }: AxiosError<IResponse<ErrorData>>) {
      self.isBrand = false;
      const msg = response?.data.message || 'Internal server error';
      self.alertStore.showAlert(msg);
      throw new Error(msg);
    },
  }))
  .actions((self) => ({
    getBrandsData: flow(function* (query: Partial<IQueryBrandData>) {
      const { search, createdByData, isDeleted, ...others } = { ...self.brandsQuery, ...query };
      if (!query.offset) self.updateBrandsQuery(query);
      const queryStr = getQueryString({
        ...others,
        search: encodeURIComponent(search || ''),
        createdBy: createdByData?.id,
        isDeleted: isDeleted?.id,
      });
      yield axios
        .get(`brands?${queryStr}`)
        .then(({ data: { data } }) => {
          return query.offset ? self.addBrands(data) : self.setBrands(data);
        })
        .catch(self.errorHandling);
    }),
    changeBrandStatus: flow(function* ({ id, status }: StatusTypeReq) {
      yield axios
        .patch(`brands/status`, { id, status })
        .then(() => {
          if (self.brands) {
            self.setBrands(self.brands.map((item) => (item.id === id ? { ...item, status } : item)));
            // self.setBrands({
            //   count: self.brandsCount,
            //   results: self.brands.map((item) => (item.id === id ? { ...item, status } : item)),
            // });
          }
        })
        .catch(self.errorHandling);
      return id;
    }),
    getAutoCompleteBrands: flow(function* (search: string) {
      const res = yield axios
        .get(`brands/autocomplete?search=${encodeURIComponent(search)}`)
        .then(({ data: { data } }) => data)
        .catch(self.errorHandling);
      return res || [];
    }),
    getAutoCompleteParsersBrands: flow(function* ({ search, type }: IParsersBrandsAutocompleteQuery) {
      const res = yield axios
        .get(`brands/crawlers/autocomplete?search=${encodeURIComponent(search)}&type=${type}`)
        .then(({ data: { data } }) => data)
        .catch(self.errorHandling);
      return res || [];
    }),
    getBrandProductCounts: flow(function* () {
      return yield axios
        .get(`brands/elastic-product-counts`)
        .then(({ data: { data } }) => {
          if (data) {
            const countsMap = Object.fromEntries(
              data.map((item: { brandId: string; productCount: number }) => [item.brandId, item.productCount])
            ) as Record<string, number>;

            self.setElasticCountsMap(countsMap);

            if (self.brands) {
              const updatedBrands = self.brands.map((brand) => ({
                ...brand,
                numberOfProductsInElastic: countsMap[brand.id] ?? 0,
              }));
              self.setBrands(updatedBrands);
            }
          }
          return data;
        })
        .catch(self.errorHandling);
    }),
  }))
  // Brand BASIC DATA
  .actions((self) => ({
    getBrandBasicData: flow(function* (id: string) {
      return yield axios
        .get(`brands/${id}`)
        .then((res) => {
          return self.setBasicBrandData(res.data.data);
        })
        .catch(self.errorHandling);
    }),
    addBrand: flow(function* (data: IBrandBasicForm) {
      const newBrand = yield axios
        .post(`brands`, filterPOSTData(data))
        .then((res) => self.setBasicBrandData(res.data.data))
        .catch(self.errorHandling);
      return newBrand;
    }),
    changeBrand: flow(function* (data: IBrandBasicForm) {
      yield axios
        .patch(`brands`, filterPOSTData(data))
        .then((res) => self.setBasicBrandData(res.data.data))
        .catch(self.errorHandling);
      return null;
    }),
    deleteBrand: flow(function* (id: string) {
      yield axios
        .delete(`brands/delete/${id}`)
        .then(() => {
          if (self.brands) {
            self.setBrands(self.brands.filter((item) => item.id !== id));
          }
        })
        .catch(self.errorHandling);
    }),
  }))
  // Brand Catalogues DATA
  .actions((self) => ({
    getBrandCatalogues: flow(function* (id: string) {
      yield axios
        .get(`news-paper/${id}`)
        .then((res) => self.setBrandsCatalogues(res.data.data))
        .catch(self.errorHandling);
    }),
    getBrandCatalogue: flow(function* (id: string) {
      if (self.brandBasicData) {
        yield axios
          .get(`news-paper/${self.brandBasicData.id}/newspapers/${id}`)
          .then((res) => self.setBrandsCatalogue(res.data.data))
          .catch(self.errorHandling);
      }
    }),
    editBrandCatalogue: flow(function* (data: IBrandsCatalogueWithPages) {
      if (self.brandBasicData) {
        yield axios
          .patch(`news-paper/${self.brandBasicData.id}/newspapers/${data.id}`, data)
          .then((res) => self.setBrandsCatalogue(res.data.data))
          .catch(self.errorHandling);
      }
    }),
    changeBrandCatalogueApprovedStatus: flow(function* ({
      id,
      isApproved,
    }: Pick<IBrandsCatalogueWithPages, 'id' | 'isApproved'>) {
      yield axios
        .patch(`news-paper/update-approve-status/${id}`, { isApproved })
        .then(() => {
          if (self.brandsCatalogue) {
            self.setBrandsCatalogue({ ...self.brandsCatalogue, isApproved });
          }
        })
        .catch(self.errorHandling);
    }),
    changeBrandPolygonsStatus: flow(function* ({
      id,
      isPolygonsWithProducts,
    }: Pick<IBrandListData, 'id' | 'isPolygonsWithProducts'>) {
      yield axios
        .patch(`brands/polygons-status/${id}`, { isPolygonsWithProducts })
        .then(() => {
          if (self.brands) {
            self.setBrands(self.brands.map((item) => (item.id === id ? { ...item, isPolygonsWithProducts } : item)));
          }
        })
        .catch(self.errorHandling);
    }),
    changeBrandCatalogueDublicatedStatus: flow(function* ({
      id,
      isDuplicated,
    }: Pick<IBrandsCatalogue, 'id' | 'isDuplicated'>) {
      yield axios
        .patch(`news-paper/update-is-duplicated-status/${id}`, { isDuplicated })
        .then(() => {
          if (self.brandsCatalogues) {
            self.setBrandsCatalogues(
              self.brandsCatalogues.map((item) => (item.id === id ? { ...item, isDuplicated } : item))
            );
          }
          if (self.brandsCatalogue && self.brandsCatalogue.id === id) {
            self.setBrandsCatalogue({ ...self.brandsCatalogue, isDuplicated });
          }
        })
        .catch(self.errorHandling);
    }),
  }));

export type IBrandsStore = Instance<typeof BrandsStore>;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const withBrandStore = (self: IStateTreeNode) => {
  return {
    views: {
      get brandsStore(): IBrandsStore {
        return getRoot<IRootStore>(self).brandsStore;
      },
    },
  };
};
